import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback, useState } from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import { POLICY } from '@/assets/md/policy';
import { TERMS } from '@/assets/md/terms';
import Flex, { ColumnFlex } from '@/components/Flex';
import Section from '@/components/Section';
import useDeviceSize from '@/hooks/mobile/useDeviceSize';

const TermsWrapper = styled(Markdown)`
  a {
    text-decoration: underline;
  }

  hr {
    margin: 28px 0;
    ${({ theme }) => theme.border[1]}
    border-top: 0;
  }

  h2 {
    font-size: ${({ theme }) => theme.size.md};
    ${({ theme }) => theme.font.bold};
    line-height: 32px;
  }

  p {
    font-size: ${({ theme }) => theme.size.sm};
    ${({ theme }) => theme.font.regular};
  }

  h2 + p {
    margin-top: 8px;
  }

  ol,
  ul {
    margin-top: 24px;
    padding-left: 32px;

    li + li {
      margin-top: 24px;
    }

    li {
      strong {
        color: ${({ theme }) => theme.colors.text};
      }

      p {
        color: #888;
      }
    }
  }

  p + ol,
  p + ul,
  ol + h2,
  ul + h2,
  ul + p {
    margin-top: 24px;
  }

  p + p {
    margin-top: 8px;
  }
`;

const Tabs = styled(Flex)`
  & > button {
    position: relative;
  }

  /* & > button:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 7px;
    right: -46%;
    width: 2px;
    height: 12px;
    margin: 0 24px;
    background-color: ${({ theme }) => theme.colors.text};
  } */

  & > div {
    width: 2px;
    height: 12px;
    background-color: ${({ theme }) => theme.colors.text};
  }
`;
const Tab = styled.button<{ $isActive: boolean }>`
  font-size: ${({ theme }) => theme.size.md};
  ${({ theme }) => theme.font.bold};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.point : theme.colors.text};
  text-align: left;
  white-space: nowrap;

  ${({ $isActive }) =>
    $isActive &&
    `
    opacity: 1;
  `}
`;

const TITLES = ['Privacy Policy', 'Terms of Use'];
const CONTENTS = [POLICY, TERMS];

export default function Terms() {
  // device size
  const deviceSize = useDeviceSize();

  // state
  const [index, setIndex] = useState(0);

  // callback
  const handleTabClick = useCallback(
    (index: number) => () => {
      setIndex(index);
    },
    []
  );

  return (
    <Section padding={deviceSize === 'mobile' ? { x: 16 } : undefined}>
      <ColumnFlex $gap={40}>
        <Flex
          $justify="space-between"
          $direction={deviceSize === 'mobile' ? 'column-reverse' : undefined}
          $gap="sm"
          $margin={{ top: 64 }}
        >
          <Tabs $align="center" $gap={23}>
            {TITLES.map((title, titleIndex) => (
              <>
                {titleIndex > 0 && <div />}
                <Tab
                  type="button"
                  $isActive={index === titleIndex}
                  onClick={handleTabClick(titleIndex)}
                  key={titleIndex}
                >
                  {title}
                </Tab>
              </>
            ))}
          </Tabs>
        </Flex>
        <TermsWrapper>{CONTENTS[index]}</TermsWrapper>
      </ColumnFlex>
    </Section>
  );
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common', 'dialog', 'tooltip'])),
  },
});
